import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  form,
  FormField as SignalFormField,
  required,
} from '@angular/forms/signals';
import {
  SignalButton,
  SignalCheckbox,
  SignalDateField,
  SignalFormDirective,
  SignalNumberField,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { CreateDataLibraryVariableApiResponse } from '@core/api/workspace/models/createDataLibraryVariableApiResponse';
import type { CreateDataLibraryVariableDataResponse } from '@core/api/workspace/models/createDataLibraryVariableDataResponse';
import { CreateDataLibraryVariableRequestValueType } from '@core/api/workspace/models/createDataLibraryVariableRequestValueType';

export type CreateVariableValueType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean';

export interface CreateVariableOverlayData {
  valueType: CreateVariableValueType;
}

export type CreateVariableOverlayResult = CreateDataLibraryVariableDataResponse;

interface CreateVariableModel {
  name: string;
  textValue: string;
  numberValue: number | null;
  dateValue: Date | null;
  booleanValue: boolean;
}

const VALUE_TYPE_LABELS: Record<CreateVariableValueType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Boolean',
};

const REQUIRED_MESSAGE = 'This field is required';

function toIsoDateString(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'app-create-variable-overlay',
  imports: [
    SignalButton,
    SignalCheckbox,
    SignalDateField,
    SignalFormDirective,
    SignalFormField,
    SignalNumberField,
    SignalTextField,
  ],
  templateUrl: './create-variable-overlay.html',
  styleUrl: './create-variable-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateVariableOverlayComponent extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly dialogRef = inject(
    DialogRef<CreateVariableOverlayResult | null>,
  );
  private readonly data = inject<CreateVariableOverlayData>(DIALOG_DATA);

  readonly createState = new State<CreateDataLibraryVariableApiResponse>();
  readonly valueType = this.data.valueType;
  readonly typeLabel = VALUE_TYPE_LABELS[this.valueType];

  readonly model = signal<CreateVariableModel>({
    name: '',
    textValue: '',
    numberValue: null,
    dateValue: null,
    booleanValue: false,
  });

  readonly createForm = form(this.model, (schema) => {
    required(schema.name, { message: REQUIRED_MESSAGE });

    switch (this.valueType) {
      case 'text':
        required(schema.textValue, { message: REQUIRED_MESSAGE });
        break;
      case 'number':
        required(schema.numberValue, { message: REQUIRED_MESSAGE });
        break;
      case 'date':
        required(schema.dateValue, { message: REQUIRED_MESSAGE });
        break;
      case 'boolean':
        break;
    }
  });

  readonly title = computed(() => `New ${this.typeLabel.toLowerCase()} variable`);

  close(): void {
    this.dialogRef.close(null);
  }

  onSave(): void {
    if (!validateSignalForm(this.createForm)) {
      return;
    }

    const draft = this.model();
    const name = draft.name.trim();
    if (!name) {
      return;
    }

    const value = this.resolveValue(draft);
    if (value === undefined) {
      return;
    }

    this.executeRequest({
      state: this.createState,
      request: this.userAPIService.createDataLibraryVariable({
        name,
        valueType: CreateDataLibraryVariableRequestValueType[this.valueType],
        value,
      }),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        this.toasterService.success('Variable created.');
        this.dialogRef.close(response.data);
      },
    });
  }

  private resolveValue(
    draft: CreateVariableModel,
  ): string | number | boolean | undefined {
    switch (this.valueType) {
      case 'text': {
        const text = draft.textValue.trim();
        return text || undefined;
      }
      case 'number': {
        const number = draft.numberValue;
        if (number === null || number === undefined || Number.isNaN(number)) {
          return undefined;
        }
        return number;
      }
      case 'date': {
        const date = draft.dateValue;
        if (!date) {
          return undefined;
        }
        return toIsoDateString(date);
      }
      case 'boolean':
        return draft.booleanValue;
      default:
        return undefined;
    }
  }
}
